import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { heroReveal } from "@/lib/motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroBookingWidget } from "@/components/HeroBookingWidget";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { IMAGES } from "@/data/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, ArrowRight, Phone, Clock } from "lucide-react";

const primary = ["p1", "p2", "p3", "p4", "p5", "p6", "p7"];
const routes = [
  { key: "r1", to: "/services/montreal-to-quebec-city" },
  { key: "r2", to: "/services/montreal-to-ottawa" },
  { key: "r3", to: "/services/montreal-to-toronto" },
];

export default function ServiceAreas() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.serviceAreas[lang] || PAGE_SEO.serviceAreas.en), path: PAGE_SEO.serviceAreas.path, image: PAGE_SEO.serviceAreas.image, lang });
  const phone = t("hero.phone");

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[82vh] flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.cityNight} alt="Montréal skyline — service coverage area" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/72 to-black/55" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-luxe uppercase text-white/60 mb-5">{t("area.eyebrow")}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">{t("area.title")}</h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">{t("area.subtitle")}</p>
            <a href={`tel:+1${phone.replace(/[^0-9]/g, "")}`} className="inline-block mt-8">
              <Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 h-12 px-7">
                <Phone className="h-4 w-4 mr-2" /> {t("hero.call")} · {phone}
              </Button>
            </a>
          </div>
          <motion.div {...heroReveal}>
            <HeroBookingWidget />
          </motion.div>
        </div>
      </section>

      {/* Primary coverage */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-14 max-w-2xl leading-tight">{t("area.primary.title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {primary.map((p) => (
            <div key={p} className="rounded-xl border border-white/10 bg-card px-6 py-5 flex items-center gap-3 text-white/85" data-testid={`area-${p}`}>
              <MapPin className="h-5 w-5 flex-shrink-0 text-white" /> {t(`area.${p}`)}
            </div>
          ))}
        </div>
      </section>

      {/* Map + long-distance */}
      <section className="border-y border-white/10 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-xl overflow-hidden border border-white/10 h-[420px]">
            <iframe
              title={t("area.map.title")}
              data-testid="coverage-map"
              className="w-full h-full grayscale-[0.2]"
              loading="lazy"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-74.9%2C45.05%2C-72.5%2C46.05&layer=mapnik&marker=45.5017%2C-73.5673"
            />
          </div>
          <div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-white mb-4 leading-tight">{t("area.routes.title")}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-8">{t("area.note")}</p>
            <div className="space-y-3">
              {routes.map((r) => (
                <Link key={r.key} to={r.to} data-testid={`area-route-${r.key}`}
                  className="group flex items-center justify-between rounded-xl border border-white/10 bg-card px-6 py-4 hover:border-white/30 transition-colors">
                  <span className="text-white">{t(`area.${r.key}`)}</span>
                  <span className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {t(`area.${r.key}.time`)}</span>
                    <ArrowRight className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl text-white mb-12 text-center leading-tight">{t("area.faq.title")}</h2>
        <Accordion type="single" collapsible className="w-full">
          {[1, 2, 3].map((n) => (
            <AccordionItem key={n} value={`q${n}`} className="border-white/10">
              <AccordionTrigger className="text-white text-left hover:no-underline text-base">{t(`area.faq.q${n}`)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{t(`area.faq.a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10 bg-card/30">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-white leading-tight">{t("area.cta.title")}</h2>
          <p className="text-white/70 mt-4">{t("area.cta.sub")}</p>
          <Button data-testid="area-cta-btn" className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base mt-8"
            onClick={() => navigate("/book")}>
            {t("widget.submit")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
